/**
 * Whether a practitioner's institution is derived from the org tree rather than
 * from free text she wrote herself.
 *
 * Requires BOTH: the deployment has one unambiguous org root
 * (`organisationRef` present) AND this specific caller actually belongs to it
 * (`hasOrgPlacement`). A single-tenant deployment does not mean every account
 * belongs to that tenant — real accounts exist whose institution is genuinely
 * different, an external clinician or a prospect evaluating the platform from
 * another hospital. `organisationRef` alone is deployment-wide, true for every
 * caller once one org root exists (see api-service-go's
 * meResponse.OrganisationRef), so it can never by itself answer "does THIS
 * person belong here"; the caller supplies that fact from a real
 * `locationMemberships` grant.
 *
 * The same predicate ProfileEditForm and Profile.tsx lock their institution
 * field on, so neither can drift on what "org-backed, for this person" means.
 * See #2575 / #2576, and the backend's api-service-go/me_org.go
 * enrichProfileOrg, gated the same way on directLocationMemberships for the
 * prompt-facing value.
 *
 * It used to live in the onboarding interview's profile fold, because the
 * interview asked the same question of a user who had not been granted
 * anything yet. That interview is the onboarding module's content now and the
 * agent writes the profile through PUT /me, so the predicate has one home: the
 * profile screens that read it.
 */

import type { Reference } from '@/api/types';

export function isInstitutionOrgDerived(
  organisationRef: Reference | null | undefined,
  hasOrgPlacement: boolean,
): boolean {
  return !!organisationRef && hasOrgPlacement;
}
